import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  attendanceAPI,
  AttendanceRecord,
  AttendanceStatus,
  AttendanceSummary,
  AttendanceCalendar,
  AttendanceStats,
  MonthlyAttendanceReport
} from '../../services/api'

// Re-export types for convenience
export { AttendanceStatus }
export type { AttendanceRecord, AttendanceSummary, AttendanceCalendar, AttendanceStats }

export interface MonthlyReport extends MonthlyAttendanceReport {}

export interface AttendanceState {
  records: AttendanceRecord[]
  currentRecord: AttendanceRecord | null
  summary: AttendanceSummary | null
  calendar: AttendanceCalendar | null
  stats: AttendanceStats | null
  monthlyReport: MonthlyReport | null
  total: number
  isLoading: boolean
  error: string | null
  filters: {
    employee_id?: number
    start_date?: string
    end_date?: string
    status?: AttendanceStatus
    department?: string
  }
  pagination: {
    page: number
    limit: number
  }
}

const initialState: AttendanceState = {
  records: [],
  currentRecord: null,
  summary: null,
  calendar: null,
  stats: null,
  monthlyReport: null,
  total: 0,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 50,
  },
}


// Async thunks
export const fetchAttendanceRecords = createAsyncThunk(
  'attendance/fetchRecords',
  async (params: {
    employee_id?: number
    start_date?: string
    end_date?: string
    status?: AttendanceStatus
    department?: string
    skip?: number
    limit?: number
  } = {}, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.getRecords(params)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch attendance records')
    }
  }
)

export const createAttendanceRecord = createAsyncThunk(
  'attendance/createRecord',
  async (recordData: Partial<AttendanceRecord>, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.createRecord(recordData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create attendance record')
    }
  }
)

export const updateAttendanceRecord = createAsyncThunk(
  'attendance/updateRecord',
  async ({ id, data }: { id: number; data: Partial<AttendanceRecord> }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.updateRecord(id, data)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update attendance record')
    }
  }
)

export const deleteAttendanceRecord = createAsyncThunk(
  'attendance/deleteRecord',
  async (id: number, { rejectWithValue }) => {
    try {
      await attendanceAPI.deleteRecord(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete attendance record')
    }
  }
)

export const fetchAttendanceSummary = createAsyncThunk(
  'attendance/fetchSummary',
  async ({ employeeId, startDate, endDate }: { employeeId: number; startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.getSummary(employeeId, startDate, endDate)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch attendance summary')
    }
  }
)

export const fetchAttendanceCalendar = createAsyncThunk(
  'attendance/fetchCalendar',
  async ({ employeeId, month, year }: { employeeId: number; month: number; year: number }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.getCalendar(employeeId, month, year)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch attendance calendar')
    }
  }
)

export const fetchAttendanceStats = createAsyncThunk(
  'attendance/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.getStats()
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch attendance stats')
    }
  }
)

export const fetchMonthlyReport = createAsyncThunk(
  'attendance/fetchMonthlyReport',
  async ({ month, year }: { month: number; year: number }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.getMonthlyReport(month, year)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch monthly report')
    }
  }
)

export const checkInOut = createAsyncThunk(
  'attendance/checkInOut',
  async (data: { employee_id: number; action: 'check_in' | 'check_out'; notes?: string }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.checkInOut(data)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to check in/out')
    }
  }
)

export const markEmployeeAbsent = createAsyncThunk(
  'attendance/markAbsent',
  async ({ employeeId, date, reason }: { employeeId: number; date: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.markAbsent(employeeId, date, reason)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark absent')
    }
  }
)

export const markEmployeePresent = createAsyncThunk(
  'attendance/markPresent',
  async ({ employeeId, date, checkInTime, checkOutTime }: { 
    employeeId: number; 
    date: string; 
    checkInTime?: string; 
    checkOutTime?: string 
  }, { rejectWithValue }) => {
    try {
      const response = await attendanceAPI.markPresent(employeeId, date, checkInTime, checkOutTime)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to mark present')
    }
  }
)

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AttendanceState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<AttendanceState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null
    },
    clearSummary: (state) => {
      state.summary = null
    },
    clearCalendar: (state) => {
      state.calendar = null
    },
    clearStats: (state) => {
      state.stats = null
    },
    clearMonthlyReport: (state) => {
      state.monthlyReport = null
    },
    // Real-time updates
    addRecord: (state, action: PayloadAction<AttendanceRecord>) => {
      state.records.unshift(action.payload)
      state.total += 1
    },
    updateRecordInList: (state, action: PayloadAction<AttendanceRecord>) => {
      const index = state.records.findIndex(record => record.id === action.payload.id)
      if (index !== -1) {
        state.records[index] = action.payload
      }
      if (state.currentRecord?.id === action.payload.id) {
        state.currentRecord = action.payload
      }
    },
    removeRecordFromList: (state, action: PayloadAction<number>) => {
      state.records = state.records.filter(record => record.id !== action.payload)
      state.total -= 1
      if (state.currentRecord?.id === action.payload) {
        state.currentRecord = null
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch records
      .addCase(fetchAttendanceRecords.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAttendanceRecords.fulfilled, (state, action) => {
        state.isLoading = false
        state.records = action.payload
        state.total = action.payload.length
        state.error = null
      })
      .addCase(fetchAttendanceRecords.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        if (!state.records) {
          state.records = []
        }
      })
      // Create record
      .addCase(createAttendanceRecord.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createAttendanceRecord.fulfilled, (state, action) => {
        state.isLoading = false
        state.records.unshift(action.payload)
        state.total += 1
        state.error = null
      })
      .addCase(createAttendanceRecord.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update record
      .addCase(updateAttendanceRecord.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateAttendanceRecord.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.records.findIndex(record => record.id === action.payload.id)
        if (index !== -1) {
          state.records[index] = action.payload
        }
        if (state.currentRecord?.id === action.payload.id) {
          state.currentRecord = action.payload
        }
        state.error = null
      })
      .addCase(updateAttendanceRecord.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Delete record
      .addCase(deleteAttendanceRecord.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteAttendanceRecord.fulfilled, (state, action) => {
        state.isLoading = false
        state.records = state.records.filter(record => record.id !== action.payload)
        state.total -= 1
        if (state.currentRecord?.id === action.payload) {
          state.currentRecord = null
        }
        state.error = null
      })
      .addCase(deleteAttendanceRecord.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch summary
      .addCase(fetchAttendanceSummary.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAttendanceSummary.fulfilled, (state, action) => {
        state.isLoading = false
        state.summary = action.payload
        state.error = null
      })
      .addCase(fetchAttendanceSummary.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch calendar
      .addCase(fetchAttendanceCalendar.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAttendanceCalendar.fulfilled, (state, action) => {
        state.isLoading = false
        state.calendar = action.payload
        state.error = null
      })
      .addCase(fetchAttendanceCalendar.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch stats
      .addCase(fetchAttendanceStats.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAttendanceStats.fulfilled, (state, action) => {
        state.isLoading = false
        state.stats = action.payload
        state.error = null
      })
      .addCase(fetchAttendanceStats.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch monthly report
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.isLoading = false
        state.monthlyReport = action.payload
        state.error = null
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Check in/out
      .addCase(checkInOut.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(checkInOut.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(checkInOut.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Mark absent
      .addCase(markEmployeeAbsent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(markEmployeeAbsent.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(markEmployeeAbsent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Mark present
      .addCase(markEmployeePresent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(markEmployeePresent.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(markEmployeePresent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setFilters,
  clearFilters,
  setPagination,
  clearError,
  clearCurrentRecord,
  clearSummary,
  clearCalendar,
  clearStats,
  clearMonthlyReport,
  addRecord,
  updateRecordInList,
  removeRecordFromList,
} = attendanceSlice.actions

export default attendanceSlice.reducer
